import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { Label } from '@/components/admin/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { useToast } from '@/hooks/use-toast';

export default function InviteSignup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [validInvite, setValidInvite] = useState(false);
  const [inviteData, setInviteData] = useState<any>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Why the invitation can't be used, shown on this page (Laura, 16 Oct 2026: it used to bounce to the login page)
  const [problem, setProblem] = useState<string | null>(null);

  const token = (searchParams.get('token') || '').trim();

  useEffect(() => {
    const verifyInvitation = async () => {
      if (!token) {
        setProblem('This link has no invitation code. Open the button in the invitation email again, or ask your admin to send the link directly.');
        setLoading(false);
        return;
      }
      try {
        // Checked by the server, so it does not depend on what this browser is signed in as
        const res = await fetch(`/api/admin/staging/invite/${encodeURIComponent(token)}`, { cache: 'no-store' });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.ok) {
          setProblem(body.message || 'This invitation link is not valid. Ask your admin to send a new one.');
          return;
        }
        setInviteData(body);
        setValidInvite(true);
      } catch (error) {
        console.error('Error verifying invitation:', error);
        setProblem('Could not check the invitation. Check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    verifyInvitation();
  }, [token]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      // Call edge function to complete signup with role assignment
      const { data, error } = await supabaseAdmin.functions.invoke('complete-invitation-signup', {
        body: {
          token,
          name,
          password,
        },
      });

      if (error) {
        throw error;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      toast({
        title: 'Account created successfully!',
        description: 'You can now log in with your credentials',
      });

      // Auto-login the user
      const { data: sessionData, error: loginError } = await supabaseAdmin.auth.signInWithPassword({
        email: inviteData.email,
        password: password,
      });

      if (loginError) {
        toast({
          title: 'Account created',
          description: 'Please log in with your new credentials',
        });
        navigate('/lp-console');
        return;
      }

      toast({
        title: 'Account created successfully!',
        description: 'Welcome to the dashboard',
      });

      navigate('/lp-console/dashboard');

    } catch (error: any) {
      console.error('Signup error:', error);
      toast({
        title: 'Signup failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (!validInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>This invitation can&apos;t be used</CardTitle>
            <CardDescription>{problem || 'This invitation link is not valid.'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" onClick={() => navigate('/lp-console')}>Go to sign in</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Complete Your Registration</CardTitle>
          <CardDescription>
            You&apos;ve been invited to join as a {inviteData.role}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={inviteData.email}
                disabled
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Enter your full name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Create a password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your password"
              />
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
